/**
 * AppointmentFormPage
 * Multi-step form for creating and editing appointments
 *
 * @version 2025.1.0
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  Button,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Switch,
  CircularProgress,
  Alert,
  AlertTitle,
  Autocomplete,
  Skeleton,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  ListItemText,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import {
  Save,
  Cancel,
  Warning as WarningIcon,
  NavigateNext,
  NavigateBefore,
  Business,
  Person,
  CalendarMonth,
  Info,
  Check,
  Science,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PageShell, HelpButton, PrerequisiteBlock, type PrerequisiteBlockProps } from '../../components';
import { prerequisiteFromError } from '../../utils/prerequisiteFromError';
import { CompanySelectField } from '../../components/forms/CompanySelectField';
import { useCompanyContext } from '../../hooks/useCompanyContext';
import { useCompanyList } from '../../stores/companyStore';
import { useAcao } from '../../authorization/useVeredito';
import {
  Appointment,
  AppointmentExamType,
  CreateAppointmentRequest,
  UpdateAppointmentRequest,
} from '../../types/appointment';
import { ScheduleSlot } from '../../types/scheduleSlot';
import { Employee } from '../../types/employee';
import appointmentService from '../../services/appointmentService';
import { receptionService } from '../../services/receptionService';
import employeeService from '../../services/employeeService';
import { unitService } from '../../services/unitService';
import { scheduleSlotService } from '../../services/scheduleSlotService';
import { SlotSelector, ExamSelector, PeriodSelector, ClinicSlotSelector, EmployeeQuickSearch, CreateEmployeeModal, ExamReferralSection } from './components';
import { formatCPF } from '../../utils/employeeFormatters';
import appointmentExamService from '../../services/appointmentExamService';
import { providerExamService } from '../../services/providerExamService';
import { ServiceType } from '../../types/provider';
import type { LinkedClinic, OrdemChegadaAvailability } from '../../types/appointment';
import type { SuggestedExam } from '../../types/appointmentExam';
import { companyService } from '../../services/companyService';
import { providerService } from '../../services/providerService';
import { CompanyProviderFormModal } from '../companies/components/modals';
import { ApiRequestError, getApiErrorMessage } from '../../types/common';
import { mensagemDeFalha } from '../../utils/acessoNegado';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';

interface EmployeeOption {
  id: number;
  name: string;
  cpf?: string;
  companyId?: number;
  unitId?: number | null;
}

interface UnitOption {
  id: number;
  name: string;
}

// Step definitions
const STEPS = [
  { label: 'Empresa e Funcionário', icon: <Business /> },
  { label: 'Exames a Realizar', icon: <Science /> },
  { label: 'Data e Horário', icon: <CalendarMonth /> },
  { label: 'Informações Adicionais', icon: <Info /> },
];

const messageForLoadFailure = (err: unknown): string => {
  const status = err instanceof ApiRequestError ? err.status : undefined;
  if (status === 404) return 'Agendamento não encontrado.';
  return mensagemDeFalha(err, 'Não foi possível carregar o agendamento. Tente de novo.');
};

export const AppointmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(id);
  const { effectiveCompanyIdString, shouldShowCompanyField } = useCompanyContext();
  const { companies: empresasOferecidas, carregada: empresasCarregadas } = useCompanyList();

  const foraDoHorario = useAcao('appointment.book_outside_slot', 'Agendar fora do horário da agenda');
  const canBookOutsideSlot = !foraDoHorario.disabled;

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);

  // Form state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Falha ao carregar o agendamento em edição: a tela troca o formulário por este aviso.
  const [loadFailure, setLoadFailure] = useState<string | null>(null);
  // Id do agendamento cujo erro já virou toast — o efeito de carga roda duas vezes em dev.
  const notifiedLoadFailureRef = useRef<number | null>(null);
  const [prerequisite, setPrerequisite] = useState<PrerequisiteBlockProps | null>(null);

  // Form fields
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [companyId, setCompanyId] = useState<string | number | null>(null);
  const [examType, setExamType] = useState<string>(AppointmentExamType.ADMISSIONAL);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [hour, setHour] = useState<string>('');
  const [minute, setMinute] = useState<string>('');
  const [responsibleEmail, setResponsibleEmail] = useState<string>('');
  const [responsiblePhone, setResponsiblePhone] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [notice, setNotice] = useState<string>('');
  const [observation, setObservation] = useState<string>('');
  const [sendEmail, setSendEmail] = useState<boolean>(true);
  const [sendWhatsApp, setSendWhatsApp] = useState<boolean>(false);
  const [appliedExamId, setAppliedExamId] = useState<number | null>(null);

  // Funcionário que chegou pela URL e ainda precisa ser buscado para saber a empresa dele.
  // Enquanto houver um, os campos mostram espaço reservado.
  const [funcionarioPendente, setFuncionarioPendente] = useState<number | null>(null);
  const [funcionarioNaoEncontrado, setFuncionarioNaoEncontrado] = useState(false);
  // Funcionário que veio com empresa na URL: só é buscado se a lista da empresa não o trouxer.
  const funcionarioParaConferirRef = useRef<{ empresa: number; funcionario: number } | null>(null);
  const buscandoFuncionario = funcionarioPendente !== null;

  // Complementary exams state
  const [selectedExamIds, setSelectedExamIds] = useState<number[]>([]);
  const [examSuggestions, setExamSuggestions] = useState<SuggestedExam[]>([]);

  // Slot selection state
  const [bookOutsideSlot, setBookOutsideSlot] = useState<boolean>(false);
  const [outOfSlotReason, setOutOfSlotReason] = useState<string>('');
  const [scheduleSlotId, setScheduleSlotId] = useState<number | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<ScheduleSlot | null>(null);
  const [noSlotsAvailable, setNoSlotsAvailable] = useState<boolean>(false);

  // Original values for edit mode date change detection
  const [originalDate, setOriginalDate] = useState<string>('');
  const [originalScheduleSlotId, setOriginalScheduleSlotId] = useState<number | null>(null);
  const [originalHour, setOriginalHour] = useState<string>('');
  const [originalMinute, setOriginalMinute] = useState<string>('');
  const [dateChanged, setDateChanged] = useState<boolean>(false);
  const [wantsToChangeSlot, setWantsToChangeSlot] = useState<boolean>(false);

  // Appointment status (for REAGENDAR blocking)
  const [appointmentStatus, setAppointmentStatus] = useState<string>('');
  const isReagendar = appointmentStatus === 'REAGENDAR';

  // Autocomplete options
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [unitFilter, setUnitFilter] = useState<number | ''>('');

  // Clinic selection state (for Ordem de Chegada)
  const [clinics, setClinics] = useState<LinkedClinic[]>([]);
  const [loadingClinics, setLoadingClinics] = useState(false);
  const [selectedClinicId, setSelectedClinicId] = useState<number | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const [periodAvailability, setPeriodAvailability] = useState<OrdemChegadaAvailability | null>(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [selectedClinicTime, setSelectedClinicTime] = useState<string | null>(null);

  // Vínculo de clínica resolvido sem sair do wizard: abre o mesmo modal da aba de
  // Prestadores da empresa (é o vínculo empresa-prestador que alimenta a lista de clínicas
  // do agendamento, não a de responsáveis), com a lista de prestadores carregada só quando
  // o usuário realmente clica em "Vincular clínica" — a maioria dos agendamentos nunca passa por aqui.
  const [providerLinkModalOpen, setProviderLinkModalOpen] = useState(false);
  const [providerLinkModalError, setProviderLinkModalError] = useState<string | null>(null);
  const [providerLinkOptions, setProviderLinkOptions] = useState<Array<{ id: number; name: string }>>([]);

  const selectedClinic = (clinics || []).find(c => c.id === selectedClinicId) ?? null;
  const isOrdemChegada = selectedClinic?.serviceType === ServiceType.ORDEM_CHEGADA;
  const isAgendaClinica = selectedClinic?.serviceType === ServiceType.AGENDA_CLINICA;

  // Auto-preenche Endereço e Aviso com os dados do prestador ao selecionar a clínica (novos agendamentos)
  useEffect(() => {
    if (isEdit) return;
    const clinic = (clinics || []).find((c) => c.id === selectedClinicId);
    if (!clinic) return;
    const fullAddress = [
      [clinic.address, clinic.addressNumber].filter(Boolean).join(', '),
      clinic.neighborhood,
      [clinic.city, clinic.state].filter(Boolean).join('/'),
    ].filter(Boolean).join(' - ');
    setAddress(fullAddress);
    setNotice(clinic.providerNotice ?? '');
  }, [selectedClinicId, clinics, isEdit]);

  // Provider-exam linking: track which exams the clinic does NOT support
  const [clinicUnlinkedExams, setClinicUnlinkedExams] = useState<Map<number, string>>(new Map());

  // Exam referral assignments (examId → clinicId)
  const [examReferrals, setExamReferrals] = useState<Map<number, number>>(new Map());

  // Refs to preserve edit data through the effect chain
  const editClinicIdRef = useRef<number | null>(null);
  const editReferralsRef = useRef<Map<number, number>>(new Map());

  // Available dates for slot selection
  const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());
  const [loadingAvailableDates, setLoadingAvailableDates] = useState(false);
  const [hasLoadedDates, setHasLoadedDates] = useState(false);

  // Create Employee Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalInitialCpf, setCreateModalInitialCpf] = useState<string | undefined>();
  const [createModalInitialCompanyId, setCreateModalInitialCompanyId] = useState<number | undefined>();

  // Selected employee for display
  const selectedEmployee = employees.find((e) => e.id === employeeId);

  // Load appointment data for edit mode
  useEffect(() => {
    if (isEdit && id) {
      loadAppointment(parseInt(id, 10));
    }
  }, [isEdit, id]);

  // Auto-fill company when field is hidden
  useEffect(() => {
    if (effectiveCompanyIdString && !companyId) {
      setCompanyId(effectiveCompanyIdString);
    }
  }, [effectiveCompanyIdString, companyId]);

  // Pre-fill from query params (used by exam convocation "Agendar" button)
  useEffect(() => {
    if (isEdit) return;
    const qCompanyId = searchParams.get('companyId');
    const qEmployeeId = searchParams.get('employeeId');
    const qExamType = searchParams.get('examType');

    const qAppliedExamId = searchParams.get('appliedExamId');

    if (qCompanyId) setCompanyId(qCompanyId);
    if (qEmployeeId && qCompanyId) {
      setEmployeeId(Number(qEmployeeId));
      funcionarioParaConferirRef.current = { empresa: Number(qCompanyId), funcionario: Number(qEmployeeId) };
    } else if (qEmployeeId) {
      // Sem empresa na URL a lista de funcionários não carrega: o funcionário ficaria escolhido e invisível.
      setFuncionarioPendente(Number(qEmployeeId));
    }
    if (qExamType) setExamType(qExamType);
    if (qAppliedExamId) setAppliedExamId(Number(qAppliedExamId));
  }, [isEdit, searchParams]);

  // Descobre a empresa do funcionário que chegou pela URL e escolhe os dois.
  // Sem achar (excluído, sem acesso, falha de rede) ou com empresa que a tela não oferece, avisa e deixa tudo vazio.
  useEffect(() => {
    if (funcionarioPendente === null) return;
    if (shouldShowCompanyField && !empresasCarregadas) return;

    let cancelado = false;
    const buscar = async () => {
      let encontrado: Employee | null = null;
      try {
        const funcionario = await employeeService.getEmployeeById(funcionarioPendente, false);
        const empresaDele = Number(funcionario.companyId);
        const oferecida = shouldShowCompanyField
          ? empresasOferecidas.some((empresa) => Number(empresa.id) === empresaDele)
          : Number(effectiveCompanyIdString) === empresaDele;
        if (oferecida) encontrado = funcionario;
      } catch (err: unknown) {
        console.error('Error loading employee from URL:', err);
      }
      if (cancelado) return;

      if (encontrado) {
        const { id: idDele, name, cpf, companyId: empresaDele, unitId } = encontrado;
        setCompanyId(String(empresaDele));
        setEmployees((prev) =>
          prev.some((e) => Number(e.id) === Number(idDele)) ? prev : [...prev, { id: idDele, name, cpf, companyId: empresaDele, unitId }]
        );
        setUnitFilter('');
        setEmployeeId(Number(idDele));
        setFuncionarioNaoEncontrado(false);
      } else {
        setCompanyId(null);
        setEmployeeId(null);
        setFuncionarioNaoEncontrado(true);
      }
      setFuncionarioPendente(null);
    };
    buscar();

    return () => {
      cancelado = true;
    };
  }, [funcionarioPendente, shouldShowCompanyField, empresasCarregadas, empresasOferecidas, effectiveCompanyIdString]);

  // Load employees when company changes
  useEffect(() => {
    if (companyId) {
      loadEmployees(Number(companyId));
    } else {
      setEmployees([]);
    }
  }, [companyId]);

  // Load units (postos de trabalho) when company changes — used to filter employees
  useEffect(() => {
    if (companyId) {
      unitService.getUnitsByCompanyId(Number(companyId))
        .then((data) => {
          const items = Array.isArray(data) ? data : [];
          setUnits(items.map((u) => ({ id: u.id, name: u.name })));
        })
        .catch(() => setUnits([]));
    } else {
      setUnits([]);
    }
    // Reset the unit filter when the company changes (skip on initial edit load)
    if (!isEdit) setUnitFilter('');
  }, [companyId, isEdit]);

  // When the unit filter changes, clear the selected employee if it no longer matches
  useEffect(() => {
    if (isEdit) return;
    if (unitFilter === '') return;
    if (!employeeId) return;
    const current = employees.find((e) => Number(e.id) === Number(employeeId));
    if (current && Number(current.unitId) !== Number(unitFilter)) {
      setEmployeeId(null);
    }
  }, [unitFilter, employeeId, employees, isEdit]);

  // Load clinics when company changes
  useEffect(() => {
    if (companyId) {
      loadClinics(Number(companyId));
    } else {
      setClinics([]);
      setSelectedClinicId(null);
    }
  }, [companyId]);

  // Load provider exams to detect unsupported exams
  useEffect(() => {
    // Don't reset referrals if we're restoring edit data
    if (editReferralsRef.current.size === 0) {
      setExamReferrals(new Map());
    }

    if (selectedClinicId) {
      providerExamService.getForProvider(selectedClinicId).then(({ available }) => {
        const map = new Map<number, string>();
        for (const exam of available) {
          map.set(Number(exam.id), exam.examName);
        }
        setClinicUnlinkedExams(map);

        // Restore saved referrals from edit mode
        if (editReferralsRef.current.size > 0) {
          setExamReferrals(editReferralsRef.current);
          editReferralsRef.current = new Map();
        }
      }).catch(() => {
        setClinicUnlinkedExams(new Map());
      });
    } else {
      setClinicUnlinkedExams(new Map());
    }
  }, [selectedClinicId]);

  // Load availability when clinic + date change (Ordem de Chegada)
  useEffect(() => {
    if (selectedClinicId && selectedDate && isOrdemChegada) {
      loadAvailability(selectedClinicId, selectedDate);
    } else {
      setPeriodAvailability(null);
    }
  }, [selectedClinicId, selectedDate, isOrdemChegada]);

  // Load available dates when company changes (Agenda dos Profissionais only)
  useEffect(() => {
    if (companyId && !canBookOutsideSlot && !isOrdemChegada && !isAgendaClinica) {
      setHasLoadedDates(false);
      loadAvailableDates(Number(companyId));
    } else {
      setAvailableDates(new Set());
      setHasLoadedDates(false);
    }
  }, [companyId, canBookOutsideSlot, isOrdemChegada, isAgendaClinica]);

  const loadAppointment = async (appointmentId: number) => {
    try {
      setLoading(true);
      setError(null);
      setLoadFailure(null);
      const appointment: Appointment = await appointmentService.getAppointmentById(appointmentId);

      // Seed employees list with appointment's employee immediately
      // so the Autocomplete resolves the value before loadEmployees finishes
      if (appointment.employee) {
        setEmployees([appointment.employee]);
      }

      setEmployeeId(appointment.employeeId);
      setCompanyId(appointment.companyId);
      setExamType(appointment.examType);
      setAppointmentStatus(appointment.status);
      setSelectedDate(appointment.selectedDate);
      setOriginalDate(appointment.selectedDate);
      setOriginalScheduleSlotId(appointment.scheduleSlotId ?? null);
      setOriginalHour(appointment.hour ?? '');
      setOriginalMinute(appointment.minute ?? '');
      setHour(appointment.hour ?? '');
      setMinute(appointment.minute ?? '');
      setResponsibleEmail(appointment.responsibleEmail ?? '');
      setResponsiblePhone(appointment.responsiblePhone ?? '');
      setAddress(appointment.address ?? '');
      setNotice(appointment.notice ?? '');
      setObservation(appointment.observation ?? '');
      setSendEmail(appointment.sendEmail);
      setSendWhatsApp(appointment.sendWhatsApp ?? false);

      // Preserve clinic ID for loadClinics to restore
      editClinicIdRef.current = appointment.clinicId ?? null;

      // Load existing complementary exams
      if (appointment.appointmentExams?.length) {
        setSelectedExamIds(appointment.appointmentExams.map((ae) => Number(ae.occupationalExamId)));

        // Extract referral assignments (examId → clinicId) for restoration
        const refMap = new Map<number, number>();
        for (const ae of appointment.appointmentExams) {
          if (ae.referral?.clinicId) {
            refMap.set(Number(ae.occupationalExamId), Number(ae.referral.clinicId));
          }
        }
        if (refMap.size > 0) {
          editReferralsRef.current = refMap;
        }
      }
    } catch (err: unknown) {
      console.error('Error loading appointment:', err);
      const message = messageForLoadFailure(err);
      setLoadFailure(message);
      // 403 e 404 já têm explicação na própria tela; só a falha genérica ganha toast.
      const status = err instanceof ApiRequestError ? err.status : undefined;
      if (status !== 403 && status !== 404 && notifiedLoadFailureRef.current !== appointmentId) {
        notifiedLoadFailureRef.current = appointmentId;
        enqueueSnackbar(message, { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async (compId: number) => {
    try {
      setLoadingEmployees(true);
      const response = await employeeService.getEmployeesByCompany(compId);
      const lista = response ?? [];
      setEmployees(lista);
      const conferir = funcionarioParaConferirRef.current;
      if (conferir && conferir.empresa === compId) {
        funcionarioParaConferirRef.current = null;
        if (!lista.some((e) => Number(e.id) === conferir.funcionario)) {
          setFuncionarioPendente(conferir.funcionario);
        }
      }
    } catch (err) {
      console.error('Error loading employees:', err);
      // Sem a lista, o funcionário da URL ficaria escolhido e invisível: avisa e esvazia.
      const conferir = funcionarioParaConferirRef.current;
      if (conferir && conferir.empresa === compId) {
        funcionarioParaConferirRef.current = null;
        setEmployeeId(null);
        setFuncionarioNaoEncontrado(true);
      }
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Quick Search: employee found
  const handleQuickSearchFound = (employee: Employee) => {
    // Set company and employee
    setCompanyId(employee.companyId);
    // Add to employees list if not already there
    setEmployees((prev) => {
      const exists = prev.some((e) => e.id === employee.id);
      if (exists) return prev;
      return [...prev, { id: employee.id, name: employee.name, cpf: employee.cpf, companyId: employee.companyId, unitId: employee.unitId }];
    });
    // Busca explícita por CPF/matrícula deve prevalecer sobre o filtro de unidade
    setUnitFilter('');
    setEmployeeId(employee.id);
    enqueueSnackbar(`Funcionário ${employee.name} selecionado`, { variant: 'success' });
  };

  // Quick Search: employee not found — open create modal
  const handleQuickSearchNotFound = (cpf: string) => {
    setCreateModalInitialCpf(cpf || undefined);
    setCreateModalInitialCompanyId(companyId ? Number(companyId) : undefined);
    setCreateModalOpen(true);
  };

  // Create modal: employee created successfully
  const handleEmployeeCreated = (employee: { id: number; name: string; cpf: string; companyId: number }) => {
    // Set company and employee
    setCompanyId(employee.companyId);
    setEmployees((prev) => [...prev, { id: employee.id, name: employee.name, cpf: employee.cpf, companyId: employee.companyId }]);
    setEmployeeId(employee.id);
    setCreateModalOpen(false);
    enqueueSnackbar('Funcionário cadastrado com sucesso!', { variant: 'success' });
  };

  // Open create modal from "+" button next to autocomplete
  const handleOpenCreateModal = () => {
    setCreateModalInitialCpf(undefined);
    setCreateModalInitialCompanyId(companyId ? Number(companyId) : undefined);
    setCreateModalOpen(true);
  };

  const loadAvailableDates = async (compId: number) => {
    try {
      setLoadingAvailableDates(true);
      const today = new Date();
      const threeMonthsLater = new Date();
      threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);

      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const startDate = formatDate(today);
      const endDate = formatDate(threeMonthsLater);

      const dates = await scheduleSlotService.getAvailableDates(startDate, endDate, compId);
      setAvailableDates(new Set(dates));
    } catch (err) {
      console.error('Error loading available dates:', err);
      setAvailableDates(new Set());
    } finally {
      setLoadingAvailableDates(false);
      setHasLoadedDates(true);
    }
  };

  const loadClinics = async (compId: number) => {
    try {
      setLoadingClinics(true);
      const data = await appointmentService.getClinicsForCompany(compId);
      setClinics(data);
      // In edit mode, restore the original clinic from the appointment
      if (isEdit && editClinicIdRef.current) {
        const exists = data.some(c => c.id === editClinicIdRef.current);
        setSelectedClinicId(exists ? editClinicIdRef.current : (data.length === 1 ? data[0].id : null));
        editClinicIdRef.current = null;
      } else if (data.length === 1) {
        setSelectedClinicId(data[0].id);
      } else {
        setSelectedClinicId(null);
      }
    } catch (err) {
      console.error('Error loading clinics:', err);
      setClinics([]);
    } finally {
      setLoadingClinics(false);
    }
  };

  // Reaproveita o carregamento de clínicas para revalidar contra a API depois que o modal
  // de vínculo de prestador vincula uma clínica — é a tela que dispara a revalidação, não o bloco.
  const reloadClinics = async () => {
    if (companyId) {
      await loadClinics(Number(companyId));
    }
  };

  const handleOpenProviderLinkModal = async () => {
    setProviderLinkModalError(null);
    try {
      const providers = await providerService.getProvidersForDropdown();
      setProviderLinkOptions(providers);
      setProviderLinkModalOpen(true);
    } catch (err) {
      console.error('Error loading providers:', err);
      enqueueSnackbar('Erro ao carregar prestadores', { variant: 'error' });
    }
  };

  const handleProviderLinkSubmit = async (data: { providerId: string; startDate: string; endDate: string; notes: string }) => {
    if (!companyId) return;
    setProviderLinkModalError(null);
    try {
      await companyService.linkProvider(String(companyId), {
        providerId: parseInt(data.providerId, 10),
        startDate: data.startDate,
        endDate: data.endDate || undefined,
        notes: data.notes || undefined,
      });
      enqueueSnackbar('Clínica vinculada com sucesso', { variant: 'success' });
      await reloadClinics();
    } catch (err: unknown) {
      setProviderLinkModalError(getApiErrorMessage(err, 'Erro ao vincular clínica'));
      throw err;
    }
  };

  const loadAvailability = async (clinicId: number, date: string) => {
    try {
      setLoadingAvailability(true);
      const data = await appointmentService.getOrdemChegadaAvailability(clinicId, date);
      setPeriodAvailability(data);
    } catch (err) {
      console.error('Error loading availability:', err);
      setPeriodAvailability(null);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const shouldDisableDate = useCallback((date: Date): boolean => {
    // Ordem de Chegada / Hora Marcada: all future dates enabled
    if (isOrdemChegada || isAgendaClinica) return false;
    // Users with permission can book any date
    if (canBookOutsideSlot) return false;
    // While loading, don't disable dates
    if (loadingAvailableDates) return false;
    // If dates haven't been loaded yet, don't disable
    if (!hasLoadedDates) return false;
    // After loading, if no dates available, disable all dates
    if (availableDates.size === 0) return true;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return !availableDates.has(dateStr);
  }, [canBookOutsideSlot, loadingAvailableDates, hasLoadedDates, availableDates, isOrdemChegada, isAgendaClinica]);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      setSelectedDate(`${year}-${month}-${day}`);
    } else {
      setSelectedDate('');
    }
  };

  const parseDateValue = (): Date | null => {
    if (!selectedDate) return null;
    const [year, month, day] = selectedDate.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const handleSlotChange = (slotId: number | null, slot: ScheduleSlot | null) => {
    setScheduleSlotId(slotId);
    setSelectedSlot(slot);
    // O bloqueio (ex.: horário lotado) valia para a escolha anterior — trocar o horário
    // invalida o aviso, e só o próximo envio decidiria se ele ainda se aplica.
    setPrerequisite(null);

    if (slot) {
      const [hourPart, minutePart] = slot.startTime.split(':');
      setHour(hourPart || '');
      setMinute(minutePart || '00');

      if (!address && slot.location) {
        setAddress(slot.location);
      }

      setBookOutsideSlot(false);
      setOutOfSlotReason('');
    }
  };

  const handleNoSlotsAvailable = (hasNoSlots: boolean) => {
    setNoSlotsAvailable(hasNoSlots);
  };

  // Reset slot selection when date changes
  useEffect(() => {
    if (isEdit && originalDate) {
      if (selectedDate !== originalDate) {
        // Date changed from original - force re-selection of slot
        setDateChanged(true);
        setScheduleSlotId(null);
        setSelectedSlot(null);
        setHour('');
        setMinute('');
      } else {
        // Date is back to original - restore original values
        setDateChanged(false);
        if (!wantsToChangeSlot) {
          setScheduleSlotId(originalScheduleSlotId);
          setSelectedSlot(null);
          setHour(originalHour);
          setMinute(originalMinute);
        }
      }
    } else {
      setScheduleSlotId(null);
      setSelectedSlot(null);
    }
    setNoSlotsAvailable(false);
    setSelectedClinicTime(null);
    if (!canBookOutsideSlot) {
      setBookOutsideSlot(false);
      setOutOfSlotReason('');
    }
  }, [selectedDate, canBookOutsideSlot]);

  // Step validation
  const isStep1Valid = (): boolean => {
    return !!companyId && !!employeeId && !!examType && !!selectedClinicId;
  };

  const isStep2ExamsValid = (): boolean => {
    // If there are uncovered exams, all must have a referral clinic assigned
    if (unsupportedExams.length > 0) {
      return unsupportedExams.every((e) => examReferrals.has(e.examId));
    }
    return true; // Exams step is optional — 0 exams is OK
  };

  const isStep3DateValid = (): boolean => {
    if (!selectedDate) return false;
    if (isOrdemChegada) return !!selectedPeriod;
    if (isAgendaClinica) return !!selectedClinicTime;
    if (isEdit && !isReagendar && !dateChanged && !wantsToChangeSlot) return true;
    if (bookOutsideSlot) {
      return !!outOfSlotReason.trim() && !!hour && !!minute;
    }
    return !!scheduleSlotId;
  };

  const isStep4InfoValid = (): boolean => {
    return true; // All fields in step 4 are optional
  };

  const canProceed = (): boolean => {
    switch (activeStep) {
      case 0: return isStep1Valid();
      case 1: return isStep2ExamsValid();
      case 2: return isStep3DateValid();
      case 3: return isStep4InfoValid();
      default: return false;
    }
  };

  const handleNext = () => {
    if (canProceed()) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!employeeId || !companyId || !examType || !selectedDate) {
      enqueueSnackbar('Preencha todos os campos obrigatórios', { variant: 'warning' });
      return;
    }

    if (isOrdemChegada && !selectedPeriod) {
      enqueueSnackbar('Selecione um período (manhã ou tarde)', { variant: 'warning' });
      return;
    }

    if (isAgendaClinica && !selectedClinicTime) {
      enqueueSnackbar('Selecione um horário disponível', { variant: 'warning' });
      return;
    }

    if (!isOrdemChegada && !isAgendaClinica && (!isEdit || dateChanged || wantsToChangeSlot) && !bookOutsideSlot && !scheduleSlotId) {
      enqueueSnackbar('Selecione um horário disponível', { variant: 'warning' });
      return;
    }

    if (bookOutsideSlot && !outOfSlotReason.trim()) {
      enqueueSnackbar('Informe o motivo do agendamento fora do horário', { variant: 'warning' });
      return;
    }

    // Abre a guia do Kit de Atendimento de forma síncrona (apenas na criação) para
    // escapar do bloqueador de pop-up — a URL real é definida após o agendamento ser criado.
    const kitTab = !isEdit ? window.open('about:blank', '_blank') : null;

    try {
      setSaving(true);
      setError(null);
      setPrerequisite(null);

      if (isEdit && id) {
        const updateData: UpdateAppointmentRequest = {
          examType,
          selectedDate,
          hour: hour ?? undefined,
          minute: minute ?? undefined,
          responsibleEmail: responsibleEmail ?? undefined,
          responsiblePhone: responsiblePhone ?? undefined,
          address: address ?? undefined,
          notice: notice ?? undefined,
          observation: observation ?? undefined,
          sendEmail,
          sendWhatsApp,
          ...((dateChanged || wantsToChangeSlot) && scheduleSlotId ? { scheduleSlotId } : {}),
        };

        await appointmentService.updateAppointment(parseInt(id, 10), updateData);
        // Sync complementary exams
        await appointmentExamService.syncExams(parseInt(id, 10), selectedExamIds);

        // Sync referral assignments if any
        if (examReferrals.size > 0) {
          const referralPayload = Array.from(examReferrals.entries())
            .map(([examId, referralClinicId]) => ({ examId, referralClinicId }));
          await appointmentExamService.syncReferrals(parseInt(id, 10), referralPayload);
        }

        enqueueSnackbar('Agendamento atualizado com sucesso', { variant: 'success' });
      } else {
        const isNoSlotType = isOrdemChegada || isAgendaClinica;
        const createData: CreateAppointmentRequest = {
          employeeId: Number(employeeId),
          companyId: Number(companyId),
          examType,
          selectedDate,
          clinicId: selectedClinicId ?? undefined,
          hour: isOrdemChegada ? undefined : (hour ?? undefined),
          minute: isOrdemChegada ? undefined : (minute ?? undefined),
          responsibleEmail: responsibleEmail ?? undefined,
          responsiblePhone: responsiblePhone ?? undefined,
          address: address ?? undefined,
          notice: notice ?? undefined,
          observation: observation ?? undefined,
          sendEmail,
          sendWhatsApp,
          appliedExamId: appliedExamId ?? undefined,
          scheduleSlotId: isNoSlotType ? undefined : (!bookOutsideSlot && scheduleSlotId ? scheduleSlotId : undefined),
          isOutOfSlot: isNoSlotType ? undefined : bookOutsideSlot,
          outOfSlotReason: !isNoSlotType && bookOutsideSlot ? outOfSlotReason.trim() : undefined,
          examIds: selectedExamIds.length > 0 ? selectedExamIds : undefined,
          examMetadata: selectedExamIds.length > 0
            ? selectedExamIds.map((examId) => {
                const sug = examSuggestions.find((s) => s.examId === examId);
                return {
                  examId,
                  applicationLevel: sug?.applicationLevel ?? null,
                  periodicityMonths: sug?.periodicityMonths ?? null,
                };
              })
            : undefined,
          period: isOrdemChegada ? (selectedPeriod ?? undefined) : undefined,
          examReferrals: examReferrals.size > 0
            ? Array.from(examReferrals.entries()).map(([examId, referralClinicId]) => ({ examId, referralClinicId }))
            : undefined,
        };

        const result = await appointmentService.createAppointment(createData);
        enqueueSnackbar('Agendamento criado com sucesso', { variant: 'success' });
        if (result.warnings && result.warnings.length > 0) {
          for (const warning of result.warnings) {
            enqueueSnackbar(warning, { variant: 'warning', autoHideDuration: 10000 });
          }
        }

        // Abre o Kit de Atendimento (Guia/ASO/Ficha) em nova guia, na mesma origem do app.
        // Reusa o mesmo token público (VIEW_KIT) do link enviado por e-mail ao funcionário.
        try {
          const { link } = await receptionService.generateKitLink(result.appointment.id);
          // `pending` liga o polling: só quem acabou de criar o agendamento sabe
          // que há documentos a caminho. Um acesso normal ao Kit não deve pollar.
          const kitUrl = window.location.origin + new URL(link).pathname + '?pending=1';
          if (kitTab) {
            kitTab.location.href = kitUrl;
          } else {
            window.open(kitUrl, '_blank');
          }
        } catch {
          kitTab?.close();
          enqueueSnackbar('Agendamento criado, mas não foi possível abrir o Kit automaticamente.', { variant: 'warning' });
        }

        // Redirect to appointment details after creation
        navigate(`/appointments/${result.appointment.id}`);
        return;
      }

      navigate('/appointments');
    } catch (err: unknown) {
      // Fecha a guia em branco do Kit caso a criação do agendamento tenha falhado.
      kitTab?.close();
      console.error('Error saving appointment:', err);

      const prerequisiteBlock = prerequisiteFromError(err);
      if (prerequisiteBlock) {
        // Navegar aqui descartaria funcionário, exames e data já preenchidos no wizard —
        // abre em nova aba para o preenchimento sobreviver ao clique.
        setPrerequisite(
          prerequisiteBlock.resolve
            ? { ...prerequisiteBlock, resolve: { ...prerequisiteBlock.resolve, openInNewTab: true } }
            : prerequisiteBlock
        );
        return;
      }

      const errorMessage = getApiErrorMessage(err, 'Erro ao salvar agendamento');
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/appointments');
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Agendamentos', path: '/appointments' },
    { label: isEdit ? 'Editar' : 'Novo' },
  ];
  const pageTitle = isEdit ? 'Editar Agendamento' : 'Novo Agendamento';

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={pageTitle}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (loadFailure) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title={pageTitle}>
        <AvisoDeFalhaAoCarregar
          mensagem={loadFailure}
          rotuloDoBotao="Voltar para agendamentos"
          aoVoltar={() => navigate('/appointments')}
        />
      </PageShell>
    );
  }

  // Step 1: Empresa e Funcionário
  const renderStep1 = () => (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={3}>
        {funcionarioNaoEncontrado && !employeeId && !buscandoFuncionario && (
          <Grid size={12}>
            <Alert severity="warning">
              <AlertTitle>Não encontramos esse funcionário.</AlertTitle>
              Escolha a empresa e o funcionário abaixo.
            </Alert>
          </Grid>
        )}

        {/* Quick Search by CPF/Matrícula */}
        {!isEdit && (
          <Grid size={12}>
            <EmployeeQuickSearch
              onEmployeeFound={handleQuickSearchFound}
              onNotFound={handleQuickSearchNotFound}
              disabled={isEdit}
            />
          </Grid>
        )}

        {!isEdit && (
          <Grid size={12}>
            <Divider sx={{ my: 0 }}>
              <Typography variant="body2" color="text.secondary">ou selecione manualmente</Typography>
            </Divider>
          </Grid>
        )}

        {buscandoFuncionario && (
          <Grid size={12} data-testid="campos-reservados" aria-busy="true">
            <Skeleton variant="rounded" height={56} sx={{ mb: 3 }} />
            <Skeleton variant="rounded" height={56} />
          </Grid>
        )}

        {shouldShowCompanyField && (
          <Grid size={12} sx={buscandoFuncionario ? { display: 'none' } : undefined}>
            <CompanySelectField
              value={String(companyId || '')}
              onChange={(val) => {
                setCompanyId(val || null);
                setEmployeeId(null);
              }}
              disabled={isEdit}
              isEditMode={isEdit}
            />
          </Grid>
        )}

        {!isEdit && !buscandoFuncionario && units.length > 0 && (
          <Grid size={12}>
            <FormControl fullWidth disabled={!companyId}>
              <InputLabel>Unidade / Posto de trabalho</InputLabel>
              <Select
                value={unitFilter === '' ? '' : String(unitFilter)}
                label="Unidade / Posto de trabalho"
                onChange={(e) =>
                  setUnitFilter(e.target.value === '' ? '' : Number(e.target.value))
                }
              >
                <MenuItem value="">Todas as unidades</MenuItem>
                {units.map((u) => (
                  <MenuItem key={u.id} value={String(u.id)}>{u.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid size={12} sx={buscandoFuncionario ? { display: 'none' } : undefined}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              sx={{ flex: 1 }}
              options={
                unitFilter === ''
                  ? employees
                  : employees.filter((e) => Number(e.unitId) === Number(unitFilter))
              }
              loading={loadingEmployees}
              value={employees.find((e) => Number(e.id) === Number(employeeId)) || null}
              onChange={(_e, value) => setEmployeeId(value?.id ? Number(value.id) : null)}
              isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
              getOptionLabel={(option) => `${option.name}${option.cpf ? ` (${formatCPF(option.cpf)})` : ''}`}
              disabled={!companyId || isEdit}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Funcionário *"
                  required
                  helperText={!companyId ? 'Selecione uma empresa primeiro' : ''}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingEmployees ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            {!isEdit && (
              <Button
                variant="contained"
                onClick={handleOpenCreateModal}
                sx={{ height: 56, whiteSpace: 'nowrap' }}
              >
                Cadastrar
              </Button>
            )}
          </Box>
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth required>
            <InputLabel>Tipo de Exame</InputLabel>
            <Select
              value={examType}
              label="Tipo de Exame"
              onChange={(e) => setExamType(e.target.value)}
            >
              {Object.values(AppointmentExamType).map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {examType === AppointmentExamType.DEMISSIONAL && (
          <Grid size={12}>
            <Alert severity="info" icon={<Info />}>
              Quando o atendimento for concluído, o funcionário passa para a situação <strong>Demitido</strong>. Se ele ainda não tiver data de demissão, ela será a data deste agendamento. Para trazê-lo de volta, use Reativar na ficha do funcionário.
            </Alert>
          </Grid>
        )}

        {/* Clinic selection — shown after company is selected */}
        {companyId && (
          <>
            {loadingClinics ? (
              <Grid size={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Carregando clínicas...</Typography>
                </Box>
              </Grid>
            ) : clinics.length > 1 ? (
              <Grid size={12}>
                <FormControl fullWidth required>
                  <InputLabel>Clínica</InputLabel>
                  <Select
                    value={selectedClinicId ?? ''}
                    label="Clínica"
                    onChange={(e) => setSelectedClinicId(e.target.value ? Number(e.target.value) : null)}
                  >
                    <MenuItem value="">Selecione a clínica...</MenuItem>
                    {clinics.map((c) => {
                      const address = [c.neighborhood, c.city, c.state].filter(Boolean).join(', ');
                      return (
                        <MenuItem key={c.id} value={c.id}>
                          <ListItemText
                            primary={`${c.displayName} (${c.serviceType || ServiceType.AGENDA_PROFISSIONAIS})`}
                            secondary={address || undefined}
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            ) : selectedClinic ? (
              <Grid size={12}>
                <Alert severity="info" icon={<Business />}>
                  <Typography variant="body2">
                    <strong>Clínica:</strong> {selectedClinic.displayName}
                    {' '}({selectedClinic.serviceType || ServiceType.AGENDA_PROFISSIONAIS})
                  </Typography>
                </Alert>
              </Grid>
            ) : !loadingClinics ? (
              <Grid size={12}>
                <PrerequisiteBlock
                  title="Esta empresa não tem clínica vinculada"
                  reason="O agendamento precisa de uma clínica para reservar o horário e emitir o encaminhamento."
                  resolve={{
                    label: 'Vincular clínica',
                    permission: 'company.manage-responsibles',
                    mode: 'modal',
                    onResolve: handleOpenProviderLinkModal,
                  }}
                  deniedHint="Peça a um administrador para vincular uma clínica a esta empresa antes de agendar."
                />
              </Grid>
            ) : null}
          </>
        )}
      </Grid>
    </Box>
  );

  // Step 2: Exames a Realizar
  const unsupportedExams = selectedExamIds
    .filter(id => clinicUnlinkedExams.has(Number(id)))
    .map(id => ({ examId: Number(id), examName: clinicUnlinkedExams.get(Number(id))! }));

  const renderStepExams = () => (
    <Box sx={{ pt: 2 }}>
      <ExamSelector
        employeeId={employeeId}
        companyId={companyId ? Number(companyId) : null}
        examType={examType}
        selectedExamIds={selectedExamIds}
        onSelectionChange={setSelectedExamIds}
        onSuggestionsLoaded={setExamSuggestions}
        autoSelectSuggestions={!isEdit}
      />
      {unsupportedExams.length > 0 && selectedClinicId && companyId && (
        <ExamReferralSection
          unsupportedExams={unsupportedExams}
          companyId={Number(companyId)}
          mainClinicId={selectedClinicId}
          mainClinicName={selectedClinic?.displayName || 'Clinica'}
          referralAssignments={examReferrals}
          onAssignmentChange={setExamReferrals}
        />
      )}
    </Box>
  );

  // Step 3: Data e Horário
  const renderStep2 = () => {
    const step3Loading = loadingClinics || (!isOrdemChegada && !isAgendaClinica && loadingAvailableDates);

    if (step3Loading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Carregando informações de agenda...
          </Typography>
        </Box>
      );
    }

    return (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={3}>
        {isReagendar && (
          <Grid size={12}>
            <Alert severity={(dateChanged || wantsToChangeSlot) ? 'success' : 'warning'} icon={(dateChanged || wantsToChangeSlot) ? <Check /> : <WarningIcon />}>
              {(dateChanged || wantsToChangeSlot)
                ? 'Nova data/horário selecionado. Prossiga para confirmar o reagendamento.'
                : <><strong>Reagendamento solicitado</strong> — Selecione uma nova data ou horário para este agendamento.</>
              }
            </Alert>
          </Grid>
        )}
        {isEdit && !isReagendar && dateChanged && !isStep3DateValid() && (
          <Grid size={12}>
            <Alert severity="warning" icon={<WarningIcon />}>
              Você alterou a data — selecione um novo horário disponível abaixo para continuar.
            </Alert>
          </Grid>
        )}
        <Grid size={12}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DatePicker
              label="Data do Agendamento *"
              disabled={!selectedClinicId}
              value={parseDateValue()}
              onChange={handleDateChange}
              minDate={new Date()}
              shouldDisableDate={(isOrdemChegada || isAgendaClinica) ? undefined : shouldDisableDate}
              loading={!(isOrdemChegada || isAgendaClinica) && loadingAvailableDates}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  helperText: (isOrdemChegada || isAgendaClinica)
                    ? ''
                    : loadingAvailableDates
                    ? 'Carregando datas disponíveis...'
                    : !canBookOutsideSlot && availableDates.size > 0
                    ? 'Apenas datas com horários disponíveis estão habilitadas'
                    : '',
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        {/* Ordem de Chegada: Period Selector */}
        {isOrdemChegada && selectedDate && (
          <Grid size={12}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Selecione o Período
              </Typography>
              <PeriodSelector
                availability={periodAvailability}
                selectedPeriod={selectedPeriod}
                onSelectPeriod={setSelectedPeriod}
                loading={loadingAvailability}
                selectedDate={selectedDate}
              />
            </Box>
          </Grid>
        )}

        {/* Hora Marcada: Clinic Slot Selector */}
        {isAgendaClinica && selectedDate && selectedClinicId && (
          <Grid size={12}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Selecione o Horário
              </Typography>
              <ClinicSlotSelector
                clinicId={selectedClinicId}
                date={selectedDate}
                value={selectedClinicTime}
                onChange={(time) => {
                  setSelectedClinicTime(time);
                  if (time) {
                    const [h, m] = time.split(':');
                    setHour(h);
                    setMinute(m);
                  } else {
                    setHour('');
                    setMinute('');
                  }
                }}
              />
            </Box>
          </Grid>
        )}

        {/* Agenda dos Profissionais: Show current time and change button in edit mode when date hasn't changed */}
        {!isOrdemChegada && !isAgendaClinica && isEdit && !dateChanged && !wantsToChangeSlot && selectedDate && (
          <Grid size={12}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2">
                Horário atual: <strong>{hour?.padStart(2, '0') || '--'}:{minute?.padStart(2, '0') || '--'}</strong>
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setWantsToChangeSlot(true);
                  setScheduleSlotId(null);
                  setSelectedSlot(null);
                  setHour('');
                  setMinute('');
                }}
              >
                Alterar horário
              </Button>
            </Box>
          </Grid>
        )}

        {/* Agenda dos Profissionais: Slot Selection */}
        {!isOrdemChegada && !isAgendaClinica && (!isEdit || dateChanged || wantsToChangeSlot) && selectedDate && companyId && (
          <Grid size={12}>
            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">
                  Horários Disponíveis
                </Typography>
                {isEdit && wantsToChangeSlot && !dateChanged && (
                  <Button
                    size="small"
                    onClick={() => {
                      setWantsToChangeSlot(false);
                      setScheduleSlotId(originalScheduleSlotId);
                      setSelectedSlot(null);
                      setHour(originalHour);
                      setMinute(originalMinute);
                    }}
                  >
                    Cancelar alteração
                  </Button>
                )}
              </Box>

              <SlotSelector
                date={selectedDate}
                companyId={Number(companyId)}
                value={bookOutsideSlot ? null : scheduleSlotId}
                onChange={handleSlotChange}
                onNoSlotsAvailable={handleNoSlotsAvailable}
              />

              {selectedSlot && !bookOutsideSlot && (
                <Alert severity="success" sx={{ mt: 2 }} icon={<Check />}>
                  <Typography variant="body2">
                    <strong>Selecionado:</strong> {selectedSlot.doctorName} - {selectedSlot.startTime} às {selectedSlot.endTime}
                  </Typography>
                </Alert>
              )}

              {/* Book outside slot option */}
              <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                  <FormControlLabel
                    control={
                      <Tooltip title={foraDoHorario.tooltip}>
                        <span>
                          <Checkbox
                            checked={bookOutsideSlot}
                            disabled={foraDoHorario.disabled}
                            onChange={(e) => {
                              setBookOutsideSlot(e.target.checked);
                              if (e.target.checked) {
                                setScheduleSlotId(null);
                                setSelectedSlot(null);
                              }
                            }}
                            color="warning"
                            inputProps={{ 'aria-label': foraDoHorario.tooltip }}
                          />
                        </span>
                      </Tooltip>
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WarningIcon fontSize="small" color="warning" />
                        <Typography variant="body2">
                          Agendar fora do horário da agenda
                        </Typography>
                      </Box>
                    }
                  />

                  {bookOutsideSlot && (
                    <>
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid size={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Hora *</InputLabel>
                            <Select
                              value={hour}
                              label="Hora *"
                              onChange={(e) => setHour(e.target.value)}
                              required
                            >
                              {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map((h) => (
                                <MenuItem key={h} value={h}>{h}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Minuto *</InputLabel>
                            <Select
                              value={minute}
                              label="Minuto *"
                              onChange={(e) => setMinute(e.target.value)}
                              required
                            >
                              {['00', '15', '30', '45'].map((m) => (
                                <MenuItem key={m} value={m}>{m}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <TextField
                        fullWidth
                        required
                        multiline
                        rows={2}
                        label="Justificativa *"
                        value={outOfSlotReason}
                        onChange={(e) => setOutOfSlotReason(e.target.value)}
                        placeholder="Informe o motivo do agendamento fora do horário"
                        sx={{ mt: 2 }}
                        size="small"
                        error={!outOfSlotReason.trim()}
                        helperText={!outOfSlotReason.trim() ? 'Justificativa é obrigatória' : ''}
                      />
                    </>
                  )}
              </Box>

              {noSlotsAvailable && !canBookOutsideSlot && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Não há horários disponíveis para esta data. Selecione outra data.
                </Alert>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
    );
  };

  // Step 3: Informações Adicionais
  const renderStep3 = () => (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Email do Responsável"
            type="email"
            value={responsibleEmail}
            onChange={(e) => setResponsibleEmail(e.target.value)}
            placeholder="email@exemplo.com"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Telefone do Responsável"
            value={responsiblePhone}
            onChange={(e) => setResponsiblePhone(e.target.value)}
            placeholder="(11) 99999-9999"
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Endereço"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Local onde será realizado o exame"
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Aviso"
            value={notice}
            onChange={(e) => setNotice(e.target.value)}
            placeholder="Instruções importantes (ex: comparecer em jejum)"
            multiline
            rows={2}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Observação"
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            placeholder="Observações adicionais"
            multiline
            rows={2}
          />
        </Grid>

        <Grid size={12}>
          <FormControlLabel
            control={
              <Switch
                checked={sendEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSendEmail(e.target.checked)}
              />
            }
            label="Enviar notificação por email"
          />
        </Grid>

        <Grid size={12}>
          <FormControlLabel
            control={
              <Switch
                checked={sendWhatsApp}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSendWhatsApp(e.target.checked)}
              />
            }
            label="Enviar notificação por WhatsApp"
          />
        </Grid>
      </Grid>
    </Box>
  );

  // Summary panel
  const renderSummary = () => (
    <Paper sx={{ p: 2, bgcolor: 'primary.50', mb: 2 }} variant="outlined">
      <Typography variant="subtitle2" color="primary" gutterBottom>
        Resumo do Agendamento
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {selectedEmployee && (
          <Chip
            icon={<Person />}
            label={selectedEmployee.name}
            size="small"
            variant="outlined"
          />
        )}
        {examType && (
          <Chip
            label={examType}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        {selectedClinic && (
          <Chip
            icon={<Business />}
            label={`${selectedClinic.displayName} (${selectedClinic.serviceType || ServiceType.AGENDA_PROFISSIONAIS})`}
            size="small"
            variant="outlined"
          />
        )}
        {selectedExamIds.length > 0 && (
          <Chip
            icon={<Science />}
            label={`${selectedExamIds.length} exame(s)`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
        {selectedDate && (
          <Chip
            icon={<CalendarMonth />}
            label={new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR')}
            size="small"
            variant="outlined"
          />
        )}
        {selectedSlot && (
          <Chip
            label={`${selectedSlot.startTime} - ${selectedSlot.doctorName}`}
            size="small"
            color="success"
            variant="outlined"
          />
        )}
        {isAgendaClinica && selectedClinicTime && (
          <Chip
            icon={<CalendarMonth />}
            label={selectedClinicTime}
            size="small"
            color="success"
            variant="outlined"
          />
        )}
        {bookOutsideSlot && hour && minute && (
          <Chip
            icon={<WarningIcon />}
            label={`${hour}:${minute} (fora da agenda)`}
            size="small"
            color="warning"
            variant="outlined"
          />
        )}
      </Box>
    </Paper>
  );

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title={pageTitle}
      actions={<HelpButton entity="appointments" screen="novo" size="small" />}
    >
        {prerequisite && (
          <Box sx={{ mb: 3 }}>
            <PrerequisiteBlock {...prerequisite} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Summary - show when past step 1 */}
        {activeStep > 0 && renderSummary()}

        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {STEPS.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    optional={
                      index === 1 ? (
                        <Typography variant="caption">Opcional</Typography>
                      ) : index === 3 ? (
                        <Typography variant="caption">Campos opcionais</Typography>
                      ) : undefined
                    }
                  >
                    {step.label}
                  </StepLabel>
                  <StepContent>
                    {index === 0 && renderStep1()}
                    {index === 1 && renderStepExams()}
                    {index === 2 && renderStep2()}
                    {index === 3 && renderStep3()}

                    <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
                      {index > 0 && (
                        <Button
                          onClick={handleBack}
                          startIcon={<NavigateBefore />}
                          disabled={saving}
                        >
                          Voltar
                        </Button>
                      )}

                      {index === 1 && (
                        <Button
                          variant="outlined"
                          color="inherit"
                          onClick={() => {
                            setSelectedExamIds([]);
                            setActiveStep((prev) => prev + 1);
                          }}
                          endIcon={<NavigateNext />}
                          sx={{ textTransform: 'none', color: 'text.secondary' }}
                        >
                          Pular — sem exames
                        </Button>
                      )}

                      {index < STEPS.length - 1 ? (
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          endIcon={<NavigateNext />}
                          disabled={!canProceed()}
                        >
                          {index === 1 && selectedExamIds.length > 0
                            ? `Próximo (${selectedExamIds.length} exame${selectedExamIds.length > 1 ? 's' : ''})`
                            : 'Próximo'}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="success"
                          type="submit"
                          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                          disabled={saving || !canProceed()}
                        >
                          {saving ? 'Salvando...' : 'Salvar Agendamento'}
                        </Button>
                      )}

                      <Box sx={{ flex: 1 }} />

                      <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={<Cancel />}
                        onClick={handleCancel}
                        disabled={saving}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </form>
        </Paper>

      {/* Create Employee Modal */}
      <CreateEmployeeModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleEmployeeCreated}
        initialCpf={createModalInitialCpf}
        initialCompanyId={createModalInitialCompanyId}
      />

      {/* Vincular clínica sem sair do wizard — mesmo modal da aba de Prestadores da empresa */}
      <CompanyProviderFormModal
        open={providerLinkModalOpen}
        onClose={() => setProviderLinkModalOpen(false)}
        onSubmit={handleProviderLinkSubmit}
        providers={providerLinkOptions}
        error={providerLinkModalError}
      />
    </PageShell>
  );
};

export default AppointmentFormPage;
